import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft, ArrowRight, ChevronDown, Copy, Download, Edit3, Expand,
  FileArchive, FileImage, FileSpreadsheet, FileText, Folder, Heart, Move,
  MoreVertical, RotateCw, Search, Trash2, Upload, Upload as ReplaceIcon, X, ZoomIn, ZoomOut,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const CATEGORIES = [
  "RFC", "e.firma", "CSD Certificates", "Constancia Fiscal",
  "Bank Statements", "Contracts", "Invoices", "Receipts",
  "Payroll", "Insurance", "Leases", "Tax Returns", "Other",
];

const SORTS = [
  ["modified", "Última modificación"],
  ["created", "Fecha de subida"],
  ["name", "Nombre"],
  ["size", "Tamaño"],
  ["viewed", "Vistos recientes"],
];

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

function iconFor(doc) {
  const ct = doc.content_type || "";
  if (ct.startsWith("image/")) return FileImage;
  if (ct.includes("spreadsheet") || ct === "text/csv") return FileSpreadsheet;
  if (ct === "application/pdf") return FileText;
  return FileArchive;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MiBovedaPage() {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("Todos");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("modified");
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [viewing, setViewing] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const uploadInput = useRef(null);
  const replaceInput = useRef(null);
  const [uploadCategory, setUploadCategory] = useState("Other");
  const [moveTarget, setMoveTarget] = useState(null); // {ids: [...], multi: bool}
  const [dragOverCat, setDragOverCat] = useState(null);

  const activeFolder = category !== "Todos" ? category : uploadCategory;

  const refresh = useCallback(async () => {
    try {
      const [docsRes, catsRes] = await Promise.all([
        api.get("/vault/documents", { params: { q: query, category, favorite: favoriteOnly, sort }, withCredentials: true }),
        api.get("/vault/categories", { withCredentials: true }),
      ]);
      setDocs(docsRes.data);
      setCategories(catsRes.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar tus documentos.");
    }
  }, [query, category, favoriteOnly, sort]);

  useEffect(() => { const t = setTimeout(refresh, 200); return () => clearTimeout(t); }, [refresh]);

  const upload = async (files) => {
    const list = files?.length ? Array.from(files) : (files ? [files] : []);
    if (!list.length) return;
    setError("");
    const targetCategory = category !== "Todos" ? category : uploadCategory;
    try {
      for (const file of list) {
        const body = new FormData();
        body.append("file", file);
        body.append("category", targetCategory);
        await api.post("/vault/documents", body, { withCredentials: true });
      }
      setNotice(`${list.length === 1 ? "Documento subido" : `${list.length} documentos subidos`} a "${targetCategory}".`);
      refresh();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos subir el archivo.");
    } finally {
      if (uploadInput.current) uploadInput.current.value = "";
    }
  };

  const toggleSelect = (id) => setSelected((current) => {
    const next = new Set(current);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const clearSelection = () => setSelected(new Set());

  const rename = async (doc, newName) => {
    if (!newName || newName === doc.name) return;
    try {
      await api.put(`/vault/documents/${doc.id}`, { name: newName }, { withCredentials: true });
      setNotice("Nombre actualizado.");
      refresh();
      if (viewing?.id === doc.id) setViewing({ ...viewing, name: newName });
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos renombrar."); }
  };

  const toggleFavorite = async (doc) => {
    try {
      await api.put(`/vault/documents/${doc.id}`, { favorite: !doc.favorite }, { withCredentials: true });
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos actualizar."); }
  };

  const move = async (doc, newCategory) => {
    try {
      await api.put(`/vault/documents/${doc.id}`, { category: newCategory }, { withCredentials: true });
      setNotice(`Movido a "${newCategory}".`);
      refresh();
      if (viewing?.id === doc.id) setViewing({ ...viewing, category: newCategory });
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos mover."); }
  };

  const openMoveModal = (ids) => setMoveTarget({ ids, multi: ids.length > 1 });

  const confirmMove = async (target) => {
    if (!moveTarget || !target) return;
    try {
      if (moveTarget.ids.length === 1) {
        await api.put(`/vault/documents/${moveTarget.ids[0]}`, { category: target }, { withCredentials: true });
      } else {
        await api.post("/vault/documents/bulk-move", { ids: moveTarget.ids, category: target }, { withCredentials: true });
      }
      setNotice(`${moveTarget.ids.length === 1 ? "Documento movido" : `${moveTarget.ids.length} documentos movidos`} a "${target}".`);
      setMoveTarget(null);
      clearSelection();
      refresh();
      if (viewing && moveTarget.ids.includes(viewing.id)) setViewing({ ...viewing, category: target });
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos mover."); }
  };

  const handleDropOnCategory = async (event, targetCategory) => {
    event.preventDefault();
    setDragOverCat(null);
    const draggedId = event.dataTransfer.getData("text/vault-id");
    if (!draggedId || targetCategory === "Todos") return;
    const ids = selected.has(draggedId) ? [...selected] : [draggedId];
    try {
      if (ids.length === 1) {
        await api.put(`/vault/documents/${ids[0]}`, { category: targetCategory }, { withCredentials: true });
      } else {
        await api.post("/vault/documents/bulk-move", { ids, category: targetCategory }, { withCredentials: true });
      }
      setNotice(`${ids.length === 1 ? "Documento movido" : `${ids.length} documentos movidos`} a "${targetCategory}".`);
      clearSelection();
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos mover."); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`¿Eliminar "${doc.name}"?`)) return;
    try {
      await api.delete(`/vault/documents/${doc.id}`, { withCredentials: true });
      setNotice("Documento eliminado.");
      refresh();
      if (viewing?.id === doc.id) setViewing(null);
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos eliminar."); }
  };

  const duplicate = async (doc) => {
    try {
      await api.post(`/vault/documents/${doc.id}/duplicate`, {}, { withCredentials: true });
      setNotice("Documento duplicado.");
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos duplicar."); }
  };

  const replaceFile = async (doc, file) => {
    if (!file) return;
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post(`/vault/documents/${doc.id}/replace`, body, { withCredentials: true });
      setNotice("Archivo reemplazado.");
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos reemplazar."); }
  };

  const updateNotes = async (doc, notes) => {
    try {
      await api.put(`/vault/documents/${doc.id}`, { notes }, { withCredentials: true });
      if (viewing?.id === doc.id) setViewing({ ...viewing, notes });
      refresh();
    } catch (issue) { setError(issue.response?.data?.detail || "No pudimos guardar la nota."); }
  };

  const bulkDelete = async () => {
    if (!selected.size || !window.confirm(`¿Eliminar ${selected.size} documento(s)?`)) return;
    await api.post("/vault/documents/bulk-delete", { ids: [...selected] }, { withCredentials: true });
    clearSelection();
    refresh();
  };
  const bulkMove = () => selected.size && openMoveModal([...selected]);
  const bulkDownload = async () => {
    if (!selected.size) return;
    const response = await api.post("/vault/documents/bulk-download", { ids: [...selected] }, { responseType: "blob", withCredentials: true });
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url; a.download = "boveda.zip"; a.click();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  };

  const viewerIndex = viewing ? docs.findIndex((d) => d.id === viewing.id) : -1;
  const goPrev = () => viewerIndex > 0 && setViewing(docs[viewerIndex - 1]);
  const goNext = () => viewerIndex >= 0 && viewerIndex < docs.length - 1 && setViewing(docs[viewerIndex + 1]);

  return (
    <AppShell>
      <main className="page vault-drive-page">
        <header className="vault-drive-head">
          <div>
            <small className="eyebrow">MI BÓVEDA</small>
            <h1>Tu drive fiscal, <em>seguro y buscable.</em></h1>
          </div>
          <div className="vault-drive-head-actions">
            {category !== "Todos" ? (
              <div className="vault-current-folder" data-testid="vault-current-folder">
                <Folder size={15} />
                <span>Se subirá a</span>
                <b>{category}</b>
              </div>
            ) : (
              <select data-testid="vault-upload-category" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)} className="expenses-select">
                {CATEGORIES.map((cat) => <option key={cat}>{cat}</option>)}
              </select>
            )}
            <button className="expenses-add-button" data-testid="vault-upload-button" onClick={() => uploadInput.current?.click()}>
              <Upload size={17} />
              Subir archivo
            </button>
            <input ref={uploadInput} data-testid="vault-file-input" type="file" multiple style={{ display: "none" }} onChange={(e) => upload(e.target.files)} accept="application/pdf,image/*,.docx,.xlsx,.doc,.xls,.txt,.csv" />
          </div>
        </header>

        {notice && <p className="expenses-notice" data-testid="vault-notice" onAnimationEnd={() => setNotice("")}>{notice}</p>}
        {error && <p className="expenses-error" data-testid="vault-error">{error}</p>}

        <section className="vault-drive-toolbar">
          <label className="expenses-search">
            <Search size={17} />
            <input data-testid="vault-search" placeholder="Buscar por nombre, nota, OCR..." value={query} onChange={(e) => setQuery(e.target.value)} />
          </label>
          <select data-testid="vault-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="expenses-select">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button data-testid="vault-favorite-toggle" className={`vault-fav-toggle ${favoriteOnly ? "on" : ""}`} onClick={() => setFavoriteOnly((v) => !v)}>
            <Heart size={15} fill={favoriteOnly ? "#ef6670" : "none"} />
            Favoritos
          </button>
        </section>

        <section className="vault-drive-body">
          <aside className="vault-categories" data-testid="vault-categories">
            <button
              className={category === "Todos" ? "active" : ""}
              onClick={() => setCategory("Todos")}
              data-testid="cat-todos"
              onDragOver={(e) => e.preventDefault()}
            >
              <Folder size={16} />
              Todos
              <span>{docs.length}</span>
            </button>
            {categories.map((c) => (
              <button
                key={c.name}
                className={`${category === c.name ? "active" : ""} ${dragOverCat === c.name ? "drop-target" : ""}`}
                onClick={() => setCategory(c.name)}
                data-testid={`cat-${c.name}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverCat(c.name); }}
                onDragLeave={() => setDragOverCat(null)}
                onDrop={(e) => handleDropOnCategory(e, c.name)}
              >
                <Folder size={16} />
                {c.name}
                <span>{c.count}</span>
              </button>
            ))}
          </aside>

          <div className="vault-drive-main">
            {selected.size > 0 && (
              <div className="vault-bulk-bar" data-testid="vault-bulk-bar">
                <b>{selected.size} seleccionado{selected.size === 1 ? "" : "s"}</b>
                <button onClick={bulkDownload} data-testid="vault-bulk-download"><Download size={15} />Descargar ZIP</button>
                <button onClick={bulkMove} data-testid="vault-bulk-move"><Move size={15} />Mover</button>
                <button onClick={bulkDelete} data-testid="vault-bulk-delete" className="danger"><Trash2 size={15} />Eliminar</button>
                <button onClick={clearSelection} data-testid="vault-bulk-clear">Cancelar</button>
              </div>
            )}

            {!docs.length ? (
              <div className="expenses-empty" data-testid="vault-empty">
                <span><Upload size={22} /></span>
                <h2>Aún no tienes documentos aquí.</h2>
                <p>Sube tu primer archivo — PDF, imagen, Excel, Word o texto.</p>
              </div>
            ) : (
              <div className="vault-file-grid" data-testid="vault-file-grid">
                {docs.map((doc) => {
                  const Icon = iconFor(doc);
                  const isChecked = selected.has(doc.id);
                  return (
                    <article
                      key={doc.id}
                      className={`vault-file-card ${isChecked ? "selected" : ""}`}
                      data-testid={`vault-file-${doc.id}`}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/vault-id", doc.id)}
                    >
                      <input type="checkbox" data-testid={`vault-select-${doc.id}`} checked={isChecked} onChange={() => toggleSelect(doc.id)} onClick={(e) => e.stopPropagation()} />
                      <VaultCardMenu doc={doc} onMove={() => openMoveModal([doc.id])} onDuplicate={() => duplicate(doc)} onRename={() => { const n = window.prompt("Nuevo nombre:", doc.name); if (n) rename(doc, n); }} onDelete={() => remove(doc)} />
                      <button className="vault-file-body" onClick={() => setViewing(doc)} data-testid={`vault-open-${doc.id}`}>
                        <div className="vault-file-icon"><Icon size={28} /></div>
                        <b>{doc.name}</b>
                        <small>{doc.category} · {fmtSize(doc.size)}</small>
                        <small>{fmtDate(doc.updated_at)}</small>
                      </button>
                      <button className="vault-fav" data-testid={`vault-fav-${doc.id}`} onClick={() => toggleFavorite(doc)} aria-label="Favorito">
                        <Heart size={15} fill={doc.favorite ? "#ef6670" : "none"} color={doc.favorite ? "#ef6670" : "#79858c"} />
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {viewing && (
          <VaultViewer
            doc={viewing}
            index={viewerIndex}
            total={docs.length}
            onClose={() => setViewing(null)}
            onPrev={goPrev}
            onNext={goNext}
            onRename={(name) => rename(viewing, name)}
            onDelete={() => remove(viewing)}
            onMove={() => openMoveModal([viewing.id])}
            onDuplicate={() => duplicate(viewing)}
            onReplace={() => replaceInput.current?.click()}
            onFavorite={() => toggleFavorite(viewing)}
            onNotes={(notes) => updateNotes(viewing, notes)}
          />
        )}
        {moveTarget && (
          <MoveModal
            count={moveTarget.ids.length}
            currentCategory={category !== "Todos" ? category : ""}
            categories={CATEGORIES}
            onClose={() => setMoveTarget(null)}
            onConfirm={confirmMove}
          />
        )}
        <input ref={replaceInput} type="file" style={{ display: "none" }} data-testid="vault-replace-input" onChange={(e) => viewing && replaceFile(viewing, e.target.files?.[0])} accept="application/pdf,image/*,.docx,.xlsx,.doc,.xls,.txt,.csv" />
      </main>
    </AppShell>
  );
}

function VaultViewer({ doc, index, total, onClose, onPrev, onNext, onRename, onDelete, onMove, onDuplicate, onReplace, onFavorite, onNotes }) {
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(doc.notes || "");
  const fileUrl = `${BACKEND}/api/vault/documents/${doc.id}/file`;
  const isImage = (doc.content_type || "").startsWith("image/");
  const isPdf = doc.content_type === "application/pdf";
  const isText = doc.content_type === "text/plain" || doc.content_type === "text/csv";
  const isOffice = (doc.content_type || "").includes("officedocument") || (doc.content_type || "").includes("msword") || (doc.content_type || "").includes("ms-excel");

  useEffect(() => { setZoom(1); setRotate(0); setNotesDraft(doc.notes || ""); }, [doc.id, doc.notes]);

  return (
    <div className={`vault-viewer ${fullscreen ? "fullscreen" : ""}`} data-testid="vault-viewer">
      <div className="vault-viewer-topbar">
        <div className="vault-viewer-title">
          <input
            data-testid="viewer-rename"
            defaultValue={doc.name}
            onBlur={(e) => e.target.value !== doc.name && onRename(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          />
          <small>{doc.category} · {fmtSize(doc.size)} · {fmtDate(doc.updated_at)} · {index + 1}/{total}</small>
        </div>
        <div className="vault-viewer-actions">
          {isImage && <><button onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} data-testid="viewer-zoom-out"><ZoomOut size={16} /></button>
          <button onClick={() => setZoom((z) => Math.min(4, z + 0.25))} data-testid="viewer-zoom-in"><ZoomIn size={16} /></button>
          <button onClick={() => setRotate((r) => (r + 90) % 360)} data-testid="viewer-rotate"><RotateCw size={16} /></button></>}
          <button onClick={() => setFullscreen((f) => !f)} data-testid="viewer-fullscreen"><Expand size={16} /></button>
          <a href={fileUrl} download={doc.name} data-testid="viewer-download"><Download size={16} /></a>
          <button onClick={onReplace} data-testid="viewer-replace" title="Reemplazar archivo"><ReplaceIcon size={16} /></button>
          <button onClick={onDuplicate} data-testid="viewer-duplicate" title="Duplicar"><Copy size={16} /></button>
          <button onClick={onFavorite} data-testid="viewer-favorite" title="Favorito"><Heart size={16} fill={doc.favorite ? "#ef6670" : "none"} color={doc.favorite ? "#ef6670" : "currentColor"} /></button>
          <button onClick={onMove} data-testid="viewer-move" title="Mover a otra carpeta"><Move size={16} /></button>
          <button onClick={onDelete} data-testid="viewer-delete" title="Eliminar" className="danger"><Trash2 size={16} /></button>
          <button onClick={onClose} data-testid="viewer-close" title="Cerrar"><X size={18} /></button>
        </div>
      </div>
      <div className="vault-viewer-nav">
        <button onClick={onPrev} disabled={index <= 0} data-testid="viewer-prev"><ArrowLeft size={18} /> Anterior</button>
        <button onClick={onNext} disabled={index >= total - 1} data-testid="viewer-next">Siguiente <ArrowRight size={18} /></button>
      </div>
      <div className="vault-viewer-canvas">
        {isImage && <img src={fileUrl} alt={doc.name} style={{ transform: `scale(${zoom}) rotate(${rotate}deg)`, transformOrigin: "center" }} />}
        {isPdf && <iframe title={doc.name} src={`${fileUrl}#view=FitH`} />}
        {isText && <iframe title={doc.name} src={fileUrl} />}
        {isOffice && (
          <div className="vault-viewer-fallback">
            <FileText size={40} />
            <p>Los archivos Office se abren mejor descargándolos.</p>
            <a href={fileUrl} download={doc.name} className="expenses-add-button"><Download size={16} />Descargar {doc.name}</a>
          </div>
        )}
      </div>
      <div className="vault-viewer-notes">
        <label>
          Nota
          <textarea
            data-testid="viewer-notes"
            rows="2"
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            onBlur={() => notesDraft !== (doc.notes || "") && onNotes(notesDraft)}
            placeholder="Escribe una nota o comentario sobre este documento..."
          />
        </label>
      </div>
    </div>
  );
}

function VaultCardMenu({ doc, onMove, onDuplicate, onRename, onDelete }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div className="vault-card-menu" ref={menuRef}>
      <button
        data-testid={`vault-menu-${doc.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="Más acciones"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <ul data-testid={`vault-menu-list-${doc.id}`} onClick={(e) => e.stopPropagation()}>
          <li onClick={() => { onMove(); setOpen(false); }} data-testid={`vault-menu-move-${doc.id}`}><Move size={14} />Mover a carpeta</li>
          <li onClick={() => { onRename(); setOpen(false); }}><Edit3 size={14} />Renombrar</li>
          <li onClick={() => { onDuplicate(); setOpen(false); }}><Copy size={14} />Duplicar</li>
          <li onClick={() => { onDelete(); setOpen(false); }} className="danger"><Trash2 size={14} />Eliminar</li>
        </ul>
      )}
    </div>
  );
}

function MoveModal({ count, currentCategory, categories, onClose, onConfirm }) {
  const [selected, setSelected] = useState(currentCategory || categories[0]);
  return (
    <div className="income-modal-overlay" data-testid="vault-move-modal" onClick={onClose}>
      <div className="income-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} data-testid="vault-move-close" aria-label="Cerrar"><X /></button>
        <h2>Mover {count === 1 ? "el documento" : `${count} documentos`} a...</h2>
        <ul className="vault-move-list" data-testid="vault-move-list">
          {categories.map((cat) => (
            <li
              key={cat}
              data-testid={`vault-move-option-${cat}`}
              className={`${selected === cat ? "active" : ""} ${currentCategory === cat ? "current" : ""}`}
              onClick={() => setSelected(cat)}
            >
              <Folder size={16} />
              <span>{cat}</span>
              {currentCategory === cat && <small>Actual</small>}
            </li>
          ))}
        </ul>
        <button
          className="primary"
          data-testid="vault-move-confirm"
          disabled={!selected || selected === currentCategory}
          onClick={() => onConfirm(selected)}
        >
          Mover aquí
        </button>
      </div>
    </div>
  );
}

